import React, { useEffect } from 'react';
import { useTheme } from '../hooks/useTheme';

interface HomePageProps {
  setCurrentPage?: (page: string) => void;
  t: (key: string) => any;
}

const HomePage: React.FC<HomePageProps> = ({ setCurrentPage, t }) => {

  useEffect(() => {
    // Handle hash-based scrolling
    if (window.location.hash) {
      const hash = window.location.hash;
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">

      {/* Hero Section with Professional Background */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 opacity-50"></div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900 dark:text-white leading-tight">
            {t('hero.title')}
          </h1>
          <p className="text-xl md:text-2xl mb-6 text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {t('hero.subtitle')}
          </p>
          <div className="bg-blue-100 dark:bg-blue-900/30 inline-block px-4 py-2 rounded-full mb-8">
            <p className="text-lg font-semibold text-blue-800 dark:text-blue-200">{t('hero.tagline')}</p>
          </div>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button 
              className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-8 py-4 rounded-lg font-medium text-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl"
              onClick={() => window.location.href = '/#features'}
            >
              {t('hero.cta')}
            </button>
            <button
              className="border-2 border-gray-300 hover:border-gray-400 text-gray-700 hover:text-gray-900 dark:border-gray-600 dark:hover:border-gray-500 dark:text-gray-300 dark:hover:text-gray-100 px-8 py-4 rounded-lg font-medium text-lg transition-all duration-300 bg-white dark:bg-gray-800 shadow hover:shadow-md"
              onClick={() => setCurrentPage && setCurrentPage('demo')}
            >
              {t('requestDemo')}
            </button>
          </div>
        </div>
      </section>

      {/* Mission Statement with Enhanced Background */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-50/30 to-transparent dark:from-blue-900/20"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{t('ourMission')}</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              {t('mission')}
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {t('features.title')}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {t('features.description')}
            </p>
          </div>

          {/* Public APIs Section */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-blue-700 dark:text-blue-400 mb-4">{t('features.publicAPIs.title')}</h2>
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">{t('features.publicAPIs.description')}</p>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
              <ul className="space-y-3">
                {Array.isArray(t('features.publicAPIs.features')) ? t('features.publicAPIs.features').map((feature: string, idx: number) => (
                  <li key={idx} className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-900 dark:text-gray-100">{feature}</span>
                  </li>
                )) : null}
              </ul>
            </div>
          </div>

          {/* Tenant-Specific APIs Section */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-indigo-700 dark:text-indigo-400 mb-4">{t('features.tenantAPIs.title')}</h2>
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">{t('features.tenantAPIs.description')}</p>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
              <ul className="space-y-3">
                {Array.isArray(t('features.tenantAPIs.features')) ? t('features.tenantAPIs.features').map((feature: string, idx: number) => (
                  <li key={idx} className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-900 dark:text-gray-100">{feature}</span>
                  </li>
                )) : null}
              </ul>
            </div>
          </div>

          {/* Cross-Platform Applications Section */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-green-700 dark:text-green-400 mb-4">{t('features.applications.title')}</h2>
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">{t('features.applications.description')}</p>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700">
              <ul className="space-y-3">
                {Array.isArray(t('features.applications.features')) ? t('features.applications.features').map((feature: string, idx: number) => (
                  <li key={idx} className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-900 dark:text-gray-100">{feature}</span>
                  </li>
                )) : null}
              </ul>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-transparent dark:from-blue-900/10"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Pay-Per-Use Model
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  Access essential business functionality with our flexible pay-per-use pricing. No minimum commitments, pay only for what you use.
                </p>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-green-50/30 to-transparent dark:from-green-900/10"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900 dark:to-green-800 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Subscription Solutions
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  Dedicated, customized API solutions for enterprise requirements with guaranteed SLA and priority support.
                </p>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-50/30 to-transparent dark:from-purple-900/10"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900 dark:to-purple-800 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  Cross-Platform Applications
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  Innovative applications like RoboCraft Studio, MediVision AI, and SmartHome AI built on our API platform.
                </p>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-shadow duration-300 relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-50/30 to-transparent dark:from-yellow-900/10"></div>
              <div className="relative z-10">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-yellow-100 to-yellow-200 dark:from-yellow-900 dark:to-yellow-800 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  High-Tech API Endpoints
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  Advanced APIs for robotics control, AI/ML deployment, 3D printing, and IoT device management.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-900/20 to-transparent"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
                  <span className="text-white font-bold">A</span>
                </div>
                <span className="text-lg font-bold text-white">{t('appName')}</span>
              </div>
              <p className="text-gray-300 leading-relaxed">
                {t('footer.description')}
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Product</h3>
              <ul className="space-y-2">
                <li><button className="text-gray-300 hover:text-white transition-colors">Features</button></li>
                <li><button className="text-gray-300 hover:text-white transition-colors">Pricing</button></li>
                <li><button className="text-gray-300 hover:text-white transition-colors">Resources</button></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Company</h3>
              <ul className="space-y-2">
                <li><button className="text-gray-300 hover:text-white transition-colors">About</button></li>
                <li><button className="text-gray-300 hover:text-white transition-colors">Careers</button></li>
                <li><button className="text-gray-300 hover:text-white transition-colors">Contact</button></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><button className="text-gray-300 hover:text-white transition-colors">Privacy Policy</button></li>
                <li><button className="text-gray-300 hover:text-white transition-colors">Terms of Service</button></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-400">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
